import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { EmployeeService } from "~/entity/Employee/api/service";
import { EmployeeListItem } from "~/entity/Employee/model/schema";

export interface IEmployeeSelectOption {
  value: string;
  label: string;
  dealerName: string;
}

export interface IEmployeeSelectFilters {
  dealerId?: number;
  partnerId?: string;
  positionInstanceIds?: number[];
}

const PAGE_SIZE = 25;

export const toEmployeeSelectOption = (
  item: EmployeeListItem,
): IEmployeeSelectOption => ({
  value: String(item.employee.id),
  label: [item.employee.surname, item.employee.name, item.employee.middleName]
    .filter(Boolean)
    .join(" "),
  dealerName: item.dealer?.dealerName || "",
});

export const useGetEmployeesForSelect = (
  enabled = true,
  filters?: IEmployeeSelectFilters,
) => {
  const [options, setOptions] = useState<IEmployeeSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [activated, setActivated] = useState(false);
  const requestIdRef = useRef(0);

  const dealerId = filters?.dealerId;
  const partnerId = filters?.partnerId;
  const positionKey = (filters?.positionInstanceIds || []).join(",");

  const load = useCallback(
    async (pageNumber: number, searchText: string, append: boolean) => {
      const requestId = ++requestIdRef.current;

      setLoading(true);
      try {
        let currentPage = pageNumber;
        let hasMoreItems = false;
        let mapped: IEmployeeSelectOption[] = [];

        do {
          const response = await EmployeeService.GET.list({
            page: currentPage,
            count: PAGE_SIZE,
            searchText: searchText || undefined,
            dealerIds: dealerId ? [dealerId] : undefined,
            bindingTypes: partnerId ? ["PARTNER"] : undefined,
            partnerIds: partnerId ? [partnerId] : undefined,
            positionIds: positionKey
              ? positionKey.split(",").map(Number)
              : undefined,
          });
          const { items, total } = response.data;
          const pageOptions = items
            .filter((item) => !partnerId || item.organization?.id === partnerId)
            .map(toEmployeeSelectOption);

          mapped = mapped.concat(pageOptions);
          hasMoreItems =
            (currentPage + 1) * PAGE_SIZE < total && items.length > 0;

          if (requestId !== requestIdRef.current) {
            return;
          }

          currentPage += 1;
        } while (partnerId && mapped.length < PAGE_SIZE && hasMoreItems);

        setOptions((prev) => {
          const merged = append ? [...prev, ...mapped] : mapped;
          const seen = new Set<string>();
          return merged.filter(
            (option) => !seen.has(option.value) && seen.add(option.value),
          );
        });
        setPage(currentPage - 1);
        setHasMore(hasMoreItems);
      } catch (error) {
        if (requestId === requestIdRef.current) {
          console.error("Ошибка загрузки сотрудников:", error);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [dealerId, partnerId, positionKey],
  );

  useEffect(() => {
    if (!enabled) {
      requestIdRef.current += 1;
      setActivated(false);
      setOptions([]);
      setSearch("");
      setPage(0);
      setHasMore(false);
      return;
    }
    if (!activated) {
      return;
    }
    load(0, search, false);
  }, [enabled, activated, search, load]);

  const onSearch = useMemo(
    () =>
      debounce((value: string) => {
        setActivated(true);
        setSearch(value);
      }, 300),
    [],
  );

  const onOpenChange = useCallback((open: boolean) => {
    if (open) {
      setActivated(true);
    }
  }, []);

  const onScrollBottom = useCallback(() => {
    if (hasMore && !loading) {
      load(page + 1, search, true);
    }
  }, [hasMore, loading, page, search, load]);

  return { options, loading, onSearch, onScrollBottom, onOpenChange };
};
